// src/pages/creator-panel/Connector/GoogleReviews/ReviewsAnalytics.js
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../../../utils/axiosInstance";
import { hasPermission } from "../../../../utils/permissionUtils";
import {
    Box, Typography, Alert, Button as MuiButton, CircularProgress, Grid,
    Card, CardContent, Chip, LinearProgress, Divider
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";

const StatCard = ({ label, value, sub }) => (
    <Card sx={{ height: "100%" }}>
        <CardContent>
            <Typography variant="body2" color="text.secondary">{label}</Typography>
            <Typography variant="h4" fontWeight={700} sx={{ mt: 0.5 }}>{value}</Typography>
            {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
        </CardContent>
    </Card>
);

const formatDate = (iso) => {
    if (!iso) return "N/A";
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? "N/A" : d.toLocaleDateString();
};

export default function ReviewsAnalytics() {
    const { chatbotId } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [stats, setStats] = useState(null);
    const [trend, setTrend] = useState([]);
    const [unanswered, setUnanswered] = useState([]);
    const [keywords, setKeywords] = useState([]);

    const [canView, setCanView] = useState(false);
    const [checkingPermissions, setCheckingPermissions] = useState(true);

    useEffect(() => {
        const check = async () => {
            setCheckingPermissions(true);
            try {
                setCanView(await hasPermission("connectors", "view"));
            } catch (err) {
                console.error("Error checking permissions:", err);
            } finally {
                setCheckingPermissions(false);
            }
        };
        check();
    }, []);

    const fetchAll = useCallback(async () => {
        setLoading(true);
        setError("");
        const params = chatbotId ? { chatbot_id: chatbotId } : {};
        try {
            const [statsRes, trendRes, unansweredRes, keywordsRes] = await Promise.all([
                axiosInstance.get("/api/google-reviews/analytics/stats", { params }),
                axiosInstance.get("/api/google-reviews/analytics/trend", { params }),
                axiosInstance.get("/api/google-reviews/analytics/unanswered", { params }),
                axiosInstance.get("/api/google-reviews/analytics/keywords", { params }),
            ]);
            setStats(statsRes.data.data || null);
            setTrend(trendRes.data.data || []);
            setUnanswered(unansweredRes.data.data || []);
            setKeywords(keywordsRes.data.data?.keywords || []);
        } catch (err) {
            setError(err.response?.data?.message || "Failed to load analytics.");
        } finally {
            setLoading(false);
        }
    }, [chatbotId]);

    useEffect(() => {
        document.title = "Reviews Analytics | KlonIT";
        fetchAll();
    }, [fetchAll]);

    if (checkingPermissions) {
        return <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}><Typography>Checking permissions...</Typography></Box>;
    }
    if (!canView) {
        return <Alert severity="error" sx={{ mt: 6 }}><strong>Access Denied:</strong> You don't have permission to view Reviews Analytics.</Alert>;
    }

    const distribution = stats?.distribution || { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    const maxStarCount = Math.max(1, ...Object.values(distribution).map((n) => Number(n) || 0));
    const maxTrendCount = Math.max(1, ...trend.map((t) => t.count || 0));

    return (
        <Box sx={{ p: 3, mt: 10 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, flexWrap: "wrap", gap: 1 }}>
                <Box>
                    <Typography variant="h4" fontWeight={700}>Reviews Analytics</Typography>
                    <Typography variant="body2" color="text.secondary">Aggregate insight into your Google reviews</Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1 }}>
                    <MuiButton variant="outlined" startIcon={<RefreshIcon />} onClick={fetchAll} disabled={loading}>Refresh</MuiButton>
                    <MuiButton
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate(`/chatbot/${chatbotId}/connectors/google-reviews`)}
                    >
                        Back to Connector
                    </MuiButton>
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}

            {loading && (
                <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}><CircularProgress /></Box>
            )}

            {!loading && stats && (
                <>
                    {/* Stat cards */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatCard label="Total Reviews" value={stats.total} sub={`${stats.trained} trained for chatbot`} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatCard label="Average Rating" value={`${stats.avg_rating} / 5`} sub={`${stats.rated} rated reviews`} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatCard label="Reply Rate" value={`${stats.reply_rate}%`} sub={`${stats.replied} of ${stats.total} replied`} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatCard label="Unanswered" value={stats.unanswered} sub="reviews with no owner reply" />
                        </Grid>
                    </Grid>

                    <Grid container spacing={2}>
                        {/* Star distribution */}
                        <Grid item xs={12} md={6}>
                            <Card sx={{ height: "100%" }}>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>Rating Distribution</Typography>
                                    {[5, 4, 3, 2, 1].map((star) => {
                                        const count = Number(distribution[star] ?? distribution[String(star)] ?? 0);
                                        const pct = Math.round((count / maxStarCount) * 100);
                                        return (
                                            <Box key={star} sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                                                <Typography variant="body2" sx={{ width: 48 }}>{star} star</Typography>
                                                <Box sx={{ flexGrow: 1 }}>
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={pct}
                                                        color={star >= 4 ? "success" : star <= 2 ? "error" : "warning"}
                                                        sx={{ height: 10, borderRadius: 5 }}
                                                    />
                                                </Box>
                                                <Typography variant="body2" sx={{ width: 36, textAlign: "right" }}>{count}</Typography>
                                            </Box>
                                        );
                                    })}
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Monthly trend */}
                        <Grid item xs={12} md={6}>
                            <Card sx={{ height: "100%" }}>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>Monthly Trend</Typography>
                                    {trend.length === 0 && <Typography variant="body2" color="text.secondary">No trend data yet.</Typography>}
                                    {trend.map((t) => (
                                        <Box key={t.period} sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                                            <Typography variant="body2" sx={{ width: 70 }}>{t.period}</Typography>
                                            <Box sx={{ flexGrow: 1 }}>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={Math.round((t.count / maxTrendCount) * 100)}
                                                    sx={{ height: 10, borderRadius: 5 }}
                                                />
                                            </Box>
                                            <Typography variant="body2" sx={{ width: 110, textAlign: "right" }}>
                                                {t.count} review{t.count === 1 ? "" : "s"} · {t.avg_rating}★
                                            </Typography>
                                        </Box>
                                    ))}
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Low-star keywords */}
                        <Grid item xs={12} md={6}>
                            <Card sx={{ height: "100%" }}>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>Common Words in Negative Reviews</Typography>
                                    {keywords.length === 0 && <Typography variant="body2" color="text.secondary">No negative-review keywords found.</Typography>}
                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                                        {keywords.map((k) => (
                                            <Chip key={k.ngram} label={`${k.ngram} (${k.count})`} size="small" color="error" variant="outlined" />
                                        ))}
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Unanswered reviews */}
                        <Grid item xs={12} md={6}>
                            <Card sx={{ height: "100%" }}>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>Reviews Awaiting a Reply</Typography>
                                    {unanswered.length === 0 && <Typography variant="body2" color="text.secondary">All reviews have a reply. Nice work!</Typography>}
                                    {unanswered.map((r, idx) => (
                                        <Box key={r.id}>
                                            {idx > 0 && <Divider sx={{ my: 1 }} />}
                                            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                                                <Typography variant="body2" fontWeight={600}>{r.reviewer_display_name || "Anonymous"}</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {r.star_rating || 0}★ · {formatDate(r.create_time)}
                                                </Typography>
                                            </Box>
                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                {(r.comment || "(No written comment.)").slice(0, 140)}
                                            </Typography>
                                        </Box>
                                    ))}
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </>
            )}

            {!loading && !stats && !error && (
                <Alert severity="info">No review data yet. Sync your Google reviews first.</Alert>
            )}
        </Box>
    );
}
